import { useEffect, useReducer } from 'react'
import type { Design, DesignModule } from '../../shared/types/design'
import type { SiteInfosheet } from '../../shared/types/infosheet'

type DesignStyle = 'artistic' | 'pedigree'

// Default export of each site's `designs` file
type SiteDesignDefinitions = Partial<Record<DesignStyle, Record<string, DesignModule>>>

// If we import from sites we get a circular dependency
const sites = ['africa', 'apac', 'arabic', 'chinese', 'global', 'latam']

const allSiteDesigns: Record<string, Promise<{ default?: SiteDesignDefinitions }>> = {}
const designsBySite: Record<string, Record<string, Design>> = {}
const infosheetLayout: Record<string, SiteInfosheet> = {}
const designsByStyle: Record<DesignStyle, Record<string, Design>> = {
  artistic: {},
  pedigree: {},
}

function loadInfoSheetLayouts(): Promise<void[]> {
  return Promise.all(
    sites.map(async (path) => {
      infosheetLayout[path] = (await import(`../../sites/${path}/infosheet`)).default
    })
  )
}

function loadSiteDesigns(): void {
  sites.forEach((path) => {
    allSiteDesigns[path] = import(`../../sites/${path}/designs`)
  })
}

loadSiteDesigns()
const infosheetLayoutsReady = loadInfoSheetLayouts()

function processDesignDefinitions(siteName: string, designDefinitions: SiteDesignDefinitions): void {
  designsBySite[siteName] = {}
  Object.entries(designDefinitions).forEach(([designType, designsForType]) => {
    Object.entries(designsForType).forEach(([designName, designOptions]) => {
      const baseOptions = designType === 'artistic' ? { noPortraits: true, noPadding: true } : {}
      const mergedOptions = { ...baseOptions, ...designOptions.options }
      const designDef: Design = {
        to: `/${designType}/${designName}`,
        ...designOptions,
        options: mergedOptions,
      }
      designsBySite[siteName][designName] = { ...designDef }
      designsByStyle[designType][designName] = { ...designDef }
    })
  })
}

let designsReady: Promise<unknown> | null = null

// Resolves once every site's designs and infosheet layouts are loaded. Exported so tests can
// wait for loading up front instead of paying for it during their first render.
export function loadDesigns(): Promise<unknown> {
  if (!designsReady) {
    designsReady = Promise.all([
      ...Object.entries(allSiteDesigns).map(async ([siteName, designImportPromise]) => {
        const designDefinitions = (await designImportPromise).default || {}
        processDesignDefinitions(siteName, designDefinitions)
      }),
      infosheetLayoutsReady,
    ])
  }
  return designsReady
}

interface UseDesignsResult {
  designsBySite: Record<string, Record<string, Design>>
  designsByStyle: Record<DesignStyle, Record<string, Design>>
  infosheetLayout: Record<string, SiteInfosheet>
}

export default function useDesigns(): UseDesignsResult {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0)

  useEffect(() => {
    let canceled = false

    loadDesigns().then(() => {
      if (!canceled) {
        forceUpdate()
      }
    })

    return () => {
      canceled = true
    }
  }, [])

  return {
    designsBySite,
    designsByStyle,
    infosheetLayout,
  }
}
